import { useEffect, useState } from 'react';
import type { TeamPostView, TeamReply } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';

const closesIn = (iso: string, now: number) => {
  const left = new Date(iso).getTime() - now;
  if (left <= 0) return 'Closed';
  const hours = Math.floor(left / 3_600_000);
  if (hours >= 48) return `Closes in ${Math.round(hours / 24)} days`;
  if (hours >= 1) return `Closes in ${hours}h`;
  return `Closes in ${Math.max(1, Math.round(left / 60_000))} min`;
};

// the pinned row at the top of Chats (Lagos Life pins "Lagos Life team" the same way)
export function TeamRow({ team, onOpen }: { team: TeamReply | null; onOpen: () => void }) {
  const latest = team?.posts[0];
  return (
    <button type="button" className="chat-team-row" onClick={onOpen}>
      <span className="chat-team-icon" aria-hidden="true">
        📣
      </span>
      <span className="chat-row-text">
        <b>London Life team</b>
        <small>{latest ? latest.title : 'News and votes from the team'}</small>
      </span>
      {!!team?.unread && <em className="chat-unread">{team.unread}</em>}
    </button>
  );
}

function PollCard({ post, onVoted }: { post: TeamPostView; onVoted: (post: TeamPostView, note: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [now] = useState(() => Date.now());
  const open = !!post.closesAt && new Date(post.closesAt).getTime() > now;
  const canVote = open && !post.myVote;
  const vote = async (optionId: string) => {
    setBusy(true);
    setFailure(null);
    try {
      const reply = await api.chat.teamVote(post.id, optionId);
      onVoted(reply.post, reply.message);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="team-card">
      <small className="team-kind">🗳️ Vote</small>
      <h3>{post.title}</h3>
      {post.body && <p>{post.body}</p>}
      <small className="team-when">
        {post.closesAt ? closesIn(post.closesAt, now) : ''} · one vote each
        {post.totalVotes !== null && ` · ${post.totalVotes} ${post.totalVotes === 1 ? 'vote' : 'votes'}`}
      </small>
      <ul className="team-options">
        {post.options.map((option) => {
          const share =
            post.totalVotes && option.votes !== null ? Math.round((option.votes / post.totalVotes) * 100) : null;
          const mine = post.myVote === option.id;
          return (
            <li key={option.id}>
              <button
                type="button"
                className={mine ? 'mine' : undefined}
                disabled={!canVote || busy}
                aria-pressed={mine}
                onClick={() => void vote(option.id)}
              >
                {share !== null && <span className="team-bar" style={{ width: `${share}%` }} aria-hidden="true" />}
                <span className="team-option-label">
                  {mine && '✓ '}
                  {option.label}
                </span>
                {share !== null && <b>{share}%</b>}
              </button>
            </li>
          );
        })}
      </ul>
      {failure && <ErrorNotice error={failure} />}
    </article>
  );
}

// the team's posts, newest first: announcements and votes. Opening it marks it read.
export function TeamChat({ onRead }: { onRead: () => void }) {
  const [posts, setPosts] = useState<TeamPostView[] | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  useEffect(() => {
    let alive = true;
    void api.chat
      .team()
      .then((reply) => {
        if (!alive) return;
        setPosts(reply.posts);
        if (reply.unread) void api.chat.teamRead().then(onRead, () => {});
      })
      .catch((error: unknown) => alive && setFailure(describeError(error)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="chat-panel team-feed" aria-label="London Life team">
      {failure && <ErrorNotice error={failure} />}
      {posts && !posts.length && <p className="chat-empty">Nothing from the team yet.</p>}
      {posts?.map((post) =>
        post.kind === 'poll' ? (
          <PollCard
            key={post.id}
            post={post}
            onVoted={(updated, message) => {
              setPosts((current) => current?.map((item) => (item.id === updated.id ? updated : item)) ?? null);
              notify(message);
            }}
          />
        ) : (
          <article key={post.id} className="team-card">
            <small className="team-kind">📣 Announcement</small>
            <h3>{post.title}</h3>
            <p>{post.body}</p>
            <small className="team-when">
              {new Date(post.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            </small>
          </article>
        ),
      )}
    </div>
  );
}
